/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Plus, ArrowRight, Ruler, MapPin, Sparkle, Image as ImageIcon,
  Camera, Calculator,
  Globe, Users, MagnifyingGlass, X, FunnelSimple, SortAscending,
  Calendar, Clock, CheckCircle, Circle, House, Microphone,
} from "@phosphor-icons/react";
import StoryOfTheDay from "@/components/StoryOfTheDay";
import ExtensionLaunchBanner from "@/components/ExtensionLaunchBanner";
import ExtensionPasswordBanner from "@/components/ExtensionPasswordBanner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { rememberBack } from "@/utils/propertyBackTracker";
import PropertyCardActions from "@/components/PropertyCardActions";
import PullIndicator from "@/components/PullIndicator";
import { usePullToRefresh } from "@/lib/usePullToRefresh";
import { SkeletonGrid } from "@/components/Skeleton";
import useWorkspaceBadges from "@/hooks/useWorkspaceBadges";

function formatPrice(p) {
  if (!p) return "—";
  return "€ " + Number(p).toLocaleString("it-IT");
}

function authedFileUrl(path) {
  if (!path) return null;
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams({ path });
  if (token) qs.append("auth", token);
  return `${API}/files?${qs.toString()}`;
}

function statusBadge(p) {
  if (p.is_public) return { label: "Online", tone: "online" };
  if (p.has_listing) return { label: "Pausa", tone: "neutral" };
  if (p.photos_enhanced_count > 0 || p.ai_description) return { label: "Pronto", tone: "pronto" };
  if (p.photos_count > 0) return { label: "In lavorazione", tone: "pronto" };
  return { label: "Bozza", tone: "neutral" };
}

const TONES = {
  online: "bg-[#7C8B6E]/92 text-white backdrop-blur-sm",
  pronto: "bg-white/90 text-[#8C6F3D] backdrop-blur-sm",
  neutral: "bg-[#1C1C1A]/85 text-white backdrop-blur-sm",
};

// iter v28 r209 — label compatte per badge stile vetrina sulla card.
// Allineate ai 4 layout V2 attivi (vedi tokens.js + CondivisioneTab.jsx).
const STYLE_LABEL = {
  elegante: "Elegante",
  lussuoso: "Lussuoso",
  luxury: "Lussuoso",
  moderno: "Moderno",
  minimal: "Minimal",
};

// iter v28 r210 — Quick switcher: click sul badge stile → menu rapido,
// cambio stile istantaneo senza entrare nella property. Utile per A/B test
// estetici sulla vetrina pubblica.
const STYLE_OPTIONS = [
  { v: "elegante", l: "Elegante" },
  { v: "lussuoso", l: "Lussuoso" },
  { v: "moderno", l: "Moderno" },
  { v: "minimal", l: "Minimal" },
];

function StyleQuickSwitcher({ property, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const current = (property.listing_style || "elegante").toLowerCase();
  const currentLabel = STYLE_LABEL[current] || "Elegante";

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const change = async (e, value) => {
    stop(e);
    if (value === current) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.put(`/properties/${property.id}`, { listing_style: value });
      toast.success(`Stile vetrina → ${STYLE_LABEL[value]}`);
      onChanged?.();
    } catch (err) {
      toast.error("Errore durante il cambio stile");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <span className="relative ml-auto" onClick={stop}>
      <button
        type="button"
        onClick={(e) => { stop(e); setOpen((v) => !v); }}
        disabled={saving}
        className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#7C8B6E] bg-[#7C8B6E]/10 hover:bg-[#7C8B6E]/20 px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition-colors disabled:opacity-50"
        title="Stile della tua vetrina personale — clicca per cambiarlo"
        data-testid={`style-badge-${property.id}`}
      >
        {saving ? "..." : currentLabel}
        <span className="text-[8px] opacity-70">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={(e) => { stop(e); setOpen(false); }} />
          <div
            className="absolute right-0 top-full mt-1 z-40 bg-white border border-[#EAE7DF] rounded-md shadow-lg overflow-hidden min-w-[120px]"
            data-testid={`style-menu-${property.id}`}
          >
            {STYLE_OPTIONS.map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={(e) => change(e, opt.v)}
                className={`block w-full text-left px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] hover:bg-[#F1F4ED] transition-colors ${current === opt.v ? "text-[#7C8B6E] bg-[#7C8B6E]/8" : "text-[#1C1C1A]"}`}
                data-testid={`style-opt-${property.id}-${opt.v}`}
              >
                {opt.l}
                {current === opt.v && <span className="ml-1.5 text-[10px]">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

const FILTERS = [
  { id: "all", label: "Tutti" },
  { id: "online", label: "Online", match: (p) => p.is_public },
  { id: "ready", label: "Pronti", match: (p) => !p.is_public && (p.photos_enhanced_count > 0 || p.ai_description) },
  { id: "draft", label: "Bozza", match: (p) => !p.is_public && p.photos_count === 0 && !p.ai_description },
];

function TodayChecklistWidget() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [patching, setPatching] = useState({}); // { todoId: true }

  const load = async () => {
    try {
      const { data: b } = await api.get("/agenda/briefing");
      // overdue in cima, poi oggi — solo open o done (filtro dismissed/snoozed)
      const pool = [
        ...(b.overdue || []).map((t) => ({ ...t, _bucket: "overdue" })),
        ...(b.today || []).map((t) => ({ ...t, _bucket: "today" })),
      ].filter((t) => t.status === "open" || t.status === "done");
      setItems(pool);
    } catch {
      /* silent */
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleDone = async (t, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (patching[t.id]) return;
    const nextStatus = t.status === "done" ? "open" : "done";
    // Optimistic
    setItems((arr) => arr.map((x) => (x.id === t.id ? { ...x, status: nextStatus } : x)));
    setPatching((p) => ({ ...p, [t.id]: true }));
    try {
      await api.patch(`/agenda/todos/${t.id}`, { status: nextStatus });
    } catch {
      // rollback
      setItems((arr) => arr.map((x) => (x.id === t.id ? { ...x, status: t.status } : x)));
    } finally {
      setPatching((p) => { const { [t.id]: _, ...rest } = p; return rest; });
    }
  };

  if (!loaded) return null;

  // Empty state: invita l'utente a registrare il primo memo vocale.
  // Critico per nuovi utenti: senza questo, l'agenda è invisibile.
  if (items.length === 0) {
    return (
      <div className="mb-5 bg-white border border-[#E8E5DE] rounded-lg p-4 flex items-start gap-3" data-testid="today-checklist-empty">
        <div className="w-9 h-9 rounded-md bg-[#F1F4ED] flex items-center justify-center shrink-0">
          <Calendar size={16} weight="duotone" className="text-[#7C8B6E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold mb-0.5">Agenda</div>
          <div className="text-[13.5px] text-[#1C1C1A] leading-snug">
            <strong>Nessun appuntamento per oggi.</strong> Dettalo a Pronto a voce: <em>«Domani 10 visita in Brera»</em>, ci pensa lui a metterlo in agenda.
          </div>
          <Link
            to="/dashboard/agenda"
            className="inline-flex items-center gap-1 mt-1.5 text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium"
            data-testid="today-empty-cta"
          >
            Apri agenda <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  const openCount = items.filter((t) => t.status === "open").length;
  const displayed = items.slice(0, 5);
  const hasMore = items.length > 5;

  const typeColors = {
    sopralluogo: "bg-[#7C8B6E]", visita: "bg-blue-600", firma: "bg-purple-600",
    chiamata: "bg-amber-500", scadenza: "bg-red-600", riunione: "bg-indigo-600",
    comunicazione: "bg-teal-600", promemoria: "bg-[#3F3D38]",
  };

  const formatDue = (dueIso, bucket) => {
    if (!dueIso) return null;
    const d = new Date(dueIso);
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
    if (bucket === "overdue") {
      const days = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
      if (days <= 0) return time;
      return days === 1 ? "Ieri" : `${days}g fa`;
    }
    return time;
  };

  return (
    <div className="mb-5 bg-white border border-[#E8E5DE] rounded-lg overflow-hidden" data-testid="today-checklist-widget">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F1F4ED]">
        <div className="flex items-center gap-2">
          <Calendar size={14} weight="duotone" className="text-[#7C8B6E]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold">Oggi · {openCount} da fare</span>
        </div>
        <Link
          to="/dashboard/agenda"
          className="text-[11px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1"
          data-testid="today-open-agenda"
        >
          Apri agenda <ArrowRight size={10} weight="bold" />
        </Link>
      </div>
      <ul className="divide-y divide-[#F1F4ED]">
        {displayed.map((t) => {
          const isDone = t.status === "done";
          const dot = typeColors[t.event_type] || "bg-[#3F3D38]";
          const isOverdue = t._bucket === "overdue" && !isDone;
          const dueLabel = formatDue(t.due_at, t._bucket);
          return (
            <li key={t.id} className="group">
              <button
                type="button"
                onClick={(e) => toggleDone(t, e)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FAFAF7] transition-colors"
                data-testid={`today-item-${t.id}`}
                data-done={isDone ? "true" : "false"}
                disabled={patching[t.id]}
              >
                <span
                  className={`w-5 h-5 shrink-0 rounded-[5px] border-2 flex items-center justify-center transition-all ${
                    isDone
                      ? "bg-[#7C8B6E] border-[#7C8B6E]"
                      : "bg-white border-[#D5D2C8] group-hover:border-[#7C8B6E]"
                  }`}
                  aria-hidden="true"
                >
                  {isDone && (
                    <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 8 7 12 13 4" />
                    </svg>
                  )}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} aria-hidden="true"></span>
                <span className={`flex-1 min-w-0 text-[13px] leading-tight ${isDone ? "line-through text-[#9A968B]" : "text-[#1C1C1A]"}`}>
                  <span className="truncate block">{t.title}</span>
                  {dueLabel && (
                    <span className={`text-[10.5px] ${isOverdue ? "text-red-600 font-medium" : "text-[#6B6A65]"}`}>
                      {isOverdue ? "Scaduto · " : ""}{dueLabel}{t.location ? ` · ${t.location}` : ""}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <Link
          to="/dashboard/agenda"
          className="block text-center text-[11.5px] text-[#6B6A65] hover:text-[#1C1C1A] py-2 border-t border-[#F1F4ED] hover:bg-[#FAFAF7]"
        >
          + {items.length - 5} altri in agenda
        </Link>
      )}
    </div>
  );
}

function NextAppointmentWidget() {
  const [next, setNext] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Look at next 7 days, pick earliest open scheduled event
        const today = new Date();
        const offset = -today.getTimezoneOffset();
        // Use week endpoint for efficiency
        const { data: b } = await api.get("/agenda/briefing");
        // Find earliest upcoming (today + soon) still open
        const pool = [...(b.today || []), ...(b.soon || [])]
          .filter((t) => t.status === "open" && t.due_at)
          .sort((a, b) => a.due_at.localeCompare(b.due_at));
        setNext(pool[0] || null);
      } catch { /* silent */ }
      finally { setLoaded(true); }
    })();
  }, []);

  if (!loaded || !next) return null;

  const d = new Date(next.due_at);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const dKey = d.toDateString();
  const labelDay =
    dKey === today.toDateString() ? "Oggi" :
    dKey === tomorrow.toDateString() ? "Domani" :
    d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  const typeColors = {
    sopralluogo: "bg-[#7C8B6E]", visita: "bg-blue-600", firma: "bg-purple-600",
    chiamata: "bg-amber-500", scadenza: "bg-red-600", riunione: "bg-indigo-600",
    comunicazione: "bg-teal-600", promemoria: "bg-[#3F3D38]",
  };
  const dot = typeColors[next.event_type] || "bg-[#3F3D38]";
  return (
    <Link
      to="/dashboard/agenda"
      className="mb-5 flex items-center gap-4 bg-white border border-[#E8E5DE] rounded-lg px-4 py-3 hover:shadow-sm hover:border-[#C8C5BD] transition-all group"
      data-testid="next-appointment-widget"
    >
      <div className="w-11 h-11 rounded-full bg-[#FAFAF7] border border-[#E8E5DE] flex items-center justify-center shrink-0 relative">
        <Calendar size={16} className="text-[#7C8B6E]" weight="duotone" />
        <span className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full ${dot} ring-2 ring-white`}></span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-semibold">Prossimo appuntamento</div>
        <div className="text-[14px] text-[#1C1C1A] font-medium truncate" data-testid="next-appointment-title">{next.title}</div>
        <div className="text-[11.5px] text-[#6B6A65] mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 capitalize"><Clock size={10} /> {labelDay} · {time}</span>
          {next.location && <span className="truncate">· {next.location}</span>}
        </div>
      </div>
      <ArrowRight size={14} className="text-[#9A968B] group-hover:text-[#1C1C1A] group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // iter v28 r300 — Contatore "novità" per ogni immobile (Workspace badge).
  const { counts: workspaceBadges } = useWorkspaceBadges();
  // iter 303: cache localStorage delle properties per evitare lo "schermo vuoto"
  // di qualche secondo quando si torna alla dashboard dal dettaglio. Le card
  // appaiono SUBITO dalla cache, poi vengono aggiornate appena la GET /properties
  // risponde. Fix per "sono tornato in annunci e non vedo più foto e testi".
  const [properties, setProperties] = useState(() => {
    try {
      const cached = localStorage.getItem("pronto_properties_cache");
      if (cached) return JSON.parse(cached);
    } catch { /* noop */ }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try { return !localStorage.getItem("pronto_properties_cache"); }
    catch { return true; }
  });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => localStorage.getItem("pronto_welcome_dismissed") === "1");

  // v34 · 29 ago 2026 · User: «durante la generazione dell'annuncio non
  // devo poter entrare, se no si impalla». Leggiamo il flag globale
  // `pronto_in_creazione_v1` (impostato dal sopralluogo dopo /to-property)
  // e disabilitiamo click sulla card corrispondente finché non è pronta.
  // L'evento 'pronto:in-creazione' viene emesso da mark/clear così ci
  // aggiorniamo appena cambia stato.
  const [creatingPid, setCreatingPid] = useState(() => {
    try {
      const raw = localStorage.getItem("pronto_in_creazione_v1");
      return raw ? (JSON.parse(raw)?.pid || null) : null;
    } catch { return null; }
  });
  useEffect(() => {
    const readFlag = () => {
      try {
        const raw = localStorage.getItem("pronto_in_creazione_v1");
        setCreatingPid(raw ? (JSON.parse(raw)?.pid || null) : null);
      } catch { setCreatingPid(null); }
    };
    window.addEventListener("pronto:in-creazione", readFlag);
    window.addEventListener("storage", readFlag);
    return () => {
      window.removeEventListener("pronto:in-creazione", readFlag);
      window.removeEventListener("storage", readFlag);
    };
  }, []);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/properties");
      setProperties(data);
      try { localStorage.setItem("pronto_properties_cache", JSON.stringify(data)); } catch { /* quota exceeded */ }
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter v29 r213 — Pull-to-refresh nativo iOS-feel su Annunci
  const { pullDistance, isRefreshing } = usePullToRefresh(load);

  /* Toast benvenuto Pronto Publisher
   * Il content script dell'estensione (dashboard-beacon.js) setta il flag
   * `pronto_publisher_installed=1` in localStorage appena l'utente apre la
   * dashboard col plugin attivo. Mostriamo un toast verde 1 sola volta
   * (flag separato `pronto_publisher_welcomed`) per confermargli che
   * l'estensione è installata e funziona. */
  useEffect(() => {
    const welcomedKey = "pronto_publisher_welcomed";
    const checkAndToast = () => {
      try {
        if (
          localStorage.getItem("pronto_publisher_installed") === "1" &&
          localStorage.getItem(welcomedKey) !== "1"
        ) {
          localStorage.setItem(welcomedKey, "1");
          toast.success(
            "Pronto Publisher installato",
            {
              description:
                "Quando apri Idealista, Immobiliare.it, Casa.it o Subito vedrai il pannello laterale per pubblicare in 1 click.",
              duration: 7000,
            },
          );
        }
      } catch (_) { /* SSR / privacy mode */ }
    };
    checkAndToast();
    // Anche se la pagina era già aperta quando l'estensione si installa
    window.addEventListener("pronto-publisher-installed", checkAndToast);
    return () => window.removeEventListener("pronto-publisher-installed", checkAndToast);
  }, []);

  const dismissWelcome = () => {
    localStorage.setItem("pronto_welcome_dismissed", "1");
    setWelcomeDismissed(true);
  };

  // Compute setup progress from real data — banner auto-hides when complete
  const realProperties = properties.filter((p) => !p.is_demo);
  const hasProperty = realProperties.length > 0;
  const hasPhotos = realProperties.some((p) => (p.photos_enhanced_count || 0) > 0 || (p.photos_count || 0) > 0);
  const hasOnline = realProperties.some((p) => p.is_public);
  const completedSteps = [hasProperty, hasPhotos, hasOnline].filter(Boolean).length;
  const allDone = completedSteps === 3;

  const demoProp = properties.find((p) => p.is_demo);
  const isFreshAccount = (() => {
    if (!user?.created_at) return false;
    const ageHours = (new Date() - new Date(user.created_at)) / (1000 * 60 * 60);
    return ageHours < 24 * 14; // 14 days
  })();
  // Banner shows for fresh accounts ONLY when they haven't created their first property yet.
  // Once they have ≥1 real annuncio, they're on their way — clutter out.
  const showWelcome = !welcomeDismissed && !loading && isFreshAccount && !hasProperty;

  const filtered = useMemo(() => {
    let list = properties;
    const f = FILTERS.find((x) => x.id === filter);
    if (f?.match) list = list.filter(f.match);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => [p.title, p.city, p.address, p.property_type]
        .some((v) => (v || "").toLowerCase().includes(q)));
    }
    return list;
  }, [properties, search, filter]);

  const stats = useMemo(() => {
    const total = properties.length;
    const online = properties.filter((p) => p.is_public).length;
    const leads = properties.reduce((s, p) => s + (p.leads_count || 0), 0);
    const photos = properties.reduce((s, p) => s + (p.photos_enhanced_count || 0), 0);
    return { total, online, leads, photos };
  }, [properties]);

  const isEmpty = !loading && properties.length === 0;
  const counters = {
    properties_count: properties.length || null,
    leads_count: stats.leads || null,
  };

  return (
    <AppShell title="Annunci" breadcrumb="Annunci" counters={counters} action={<ReopenGuidesBtn keys={["annunci:intro-empty"]} testId="annunci-reopen-guides" />}>
      <PullIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      {/* iter v29 r274 — Onboarding checklist actionable (4 step concreti).
          Si nasconde automaticamente quando completato o dismesso. */}
      <OnboardingChecklist />
      {/* Title row */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#111827] tracking-tight" data-testid="dashboard-title">
          Annunci
        </h1>
        {isEmpty ? (
          <DismissibleIntro storageKey="annunci:intro-empty">
            <p className="mt-1 text-[13px] text-[#6B7280]" data-testid="dashboard-subtitle">
              Qui compariranno gli immobili che gestisci.
            </p>
          </DismissibleIntro>
        ) : (
          <p className="mt-1 text-[13px] text-[#6B7280]" data-testid="dashboard-subtitle">
            {`${stats.total} in gestione · ${stats.online} online · ${stats.leads} contatti`}
          </p>
        )}
      </div>

      {/* Setup banner — shows for new agents until they complete all 3 steps OR dismiss.
          Hidden on first-load empty state: the empty-state panel below already shows
          the same "create first listing" CTA, so showing both creates duplication. */}
      {showWelcome && !isEmpty && (() => {
        const firstProp = realProperties[0] || demoProp;
        const steps = [
          {
            id: "create",
            done: hasProperty,
            label: "Crea il primo annuncio",
            hint: "Bastano titolo, prezzo e zona",
            cta: hasProperty ? null : { to: "/dashboard/property/new", text: "Crea ora", testid: "banner-cta-create" },
          },
          {
            id: "photos",
            done: hasPhotos,
            label: "Carica e migliora le foto",
            hint: "Anche scatti dal cellulare — ci pensa Pronto",
            cta: !hasPhotos && firstProp ? { to: `/dashboard/property/${firstProp.id}`, text: "Carica foto", testid: "banner-cta-photos" } : null,
          },
          {
            id: "publish",
            done: hasOnline,
            label: "Rendi annuncio online",
            hint: "Pagina pubblica + condivisione WhatsApp",
            cta: !hasOnline && hasPhotos && firstProp ? { to: `/dashboard/property/${firstProp.id}`, text: "Rendi online", testid: "banner-cta-publish" } : null,
          },
        ];
        const progressPct = Math.round((completedSteps / steps.length) * 100);
        return (
          <div
            className="mb-5 bg-white border border-[#7C8B6E]/30 rounded-lg p-4 lg:p-5 relative shadow-sm"
            data-testid="welcome-banner"
            data-completed-steps={completedSteps}
          >
            <button
              onClick={dismissWelcome}
              className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center text-[#9A968B] hover:text-[#1C1C1A] hover:bg-[#F1F4ED] rounded-full transition-colors"
              data-testid="welcome-dismiss"
              aria-label="Chiudi e non mostrare più"
              title="Non mostrare più"
            >
              <X size={13} weight="bold" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-2.5 mb-3 pr-7">
              <div className="w-8 h-8 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-full flex items-center justify-center shrink-0">
                <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold">
                  Configura Pronto · {completedSteps}/3
                </div>
                <div className="text-[14px] text-[#1C1C1A] font-semibold leading-tight">
                  {completedSteps === 0 && "Primo annuncio in 3 mosse"}
                  {completedSteps === 1 && "Bene, ora aggiungi le foto"}
                  {completedSteps === 2 && "Ultimo passo: rendi l'annuncio online"}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-[#F1F4ED] rounded-full overflow-hidden mb-3.5" aria-hidden="true">
              <div
                className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
                data-testid="welcome-progress-bar"
              />
            </div>

            {/* Steps list */}
            <ol className="space-y-2" data-testid="welcome-steps">
              {steps.map((s, idx) => (
                <li
                  key={s.id}
                  className={`flex items-start gap-2.5 ${s.done ? "opacity-55" : ""}`}
                  data-testid={`welcome-step-${s.id}`}
                  data-step-done={s.done ? "true" : "false"}
                >
                  <span className="mt-0.5 shrink-0">
                    {s.done ? (
                      <CheckCircle size={18} weight="fill" className="text-[#7C8B6E]" />
                    ) : (
                      <Circle size={18} weight="regular" className="text-[#C8C5BD]" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[13px] leading-tight ${s.done ? "line-through text-[#9A968B]" : "text-[#1C1C1A] font-medium"}`}>
                      <span className="text-[10px] mr-1 text-[#9A968B]">{idx + 1}.</span>{s.label}
                    </div>
                    {!s.done && (
                      <div className="text-[11.5px] text-[#6B7280] mt-0.5">{s.hint}</div>
                    )}
                  </div>
                  {s.cta && (
                    <Link
                      to={s.cta.to}
                      className="bg-[#1C1C1A] hover:bg-black text-white px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1 text-[11.5px] transition-colors shrink-0"
                      data-testid={s.cta.testid}
                    >
                      {s.cta.text} <ArrowRight size={10} weight="bold" />
                    </Link>
                  )}
                </li>
              ))}
            </ol>

            {/* Demo example link, only on step 0 */}
            {completedSteps === 0 && demoProp && (
              <div className="mt-3.5 pt-3 border-t border-[#F1F4ED]">
                <Link
                  to={`/dashboard/property/${demoProp.id}`}
                  className="text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
                  data-testid="welcome-open-demo"
                >
                  <House size={11} weight="fill" /> Vedi un esempio già pronto (immobile demo)
                </Link>
              </div>
            )}
          </div>
        );
      })()}

      {/* iter 305 r13: Banner launch estensione Chrome ("Pronto Publisher").
       * Si nasconde automaticamente per gli agenti che hanno già installato
       * l'estensione (rileva il flag `pronto_publisher_installed=1` settato
       * dal content script). Snooze 7gg se chiuso. Posizionato DOPO il
       * welcome-banner del primo annuncio per non eclissarlo. */}
      <ExtensionLaunchBanner />
      <ExtensionPasswordBanner user={user} />

      {/* TodayChecklistWidget moved to Home — Annunci page focuses on listings */}

      {/* KPI strip */}
      {!isEmpty && !loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5" data-testid="kpi-strip">
          {[
            { label: "In gestione", val: stats.total, Icon: ImageIcon },
            { label: "Online", val: stats.online, Icon: Globe },
            { label: "Foto migliorate", val: stats.photos, Icon: Sparkle },
            { label: "Contatti", val: stats.leads, Icon: Users },
          ].map(({ label, val, Icon }) => (
            <div key={label} className="bg-white border border-[#EAE7DF] rounded-lg px-4 py-3">
              <div className="text-[11px] text-[#6B7280] font-medium mb-1">{label}</div>
              <div className="text-[22px] font-semibold text-[#111827] tabular-nums tracking-tight leading-none">{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + search */}
      {!isEmpty && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="filters-row">
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => {
              const count = f.id === "all"
                ? properties.length
                : properties.filter(f.match || (() => false)).length;
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3.5 py-2 sm:py-1.5 text-[12.5px] rounded-md font-medium inline-flex items-center gap-1.5 transition-colors min-h-[36px] ${
                    active ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#EAE7DF] text-[#374151] hover:border-[#C8C5BD]"
                  }`}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label}
                  <span className={active ? "text-white/65" : "text-[#9CA3AF]"}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2 w-full sm:w-auto">
            <div className="relative max-w-xs flex-1 sm:w-auto">
              <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Cerca..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-[12.5px] bg-white border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] focus:ring-2 focus:ring-[#7C8B6E]/15 placeholder-[#9CA3AF]"
                data-testid="search-input"
              />
            </div>
            {/* iter v28 r393 — User M1302: «nel pannello annunci da cellulare
                non c'è la possibilità di nuovo annuncio». Aggiunto bottone
                inline sempre visibile sulla destra (sia mobile che desktop),
                accanto alla search. È la posizione più scopribile in alto
                pagina, e siccome qui non siamo in empty-state, l'utente sa
                già che il bottone gli serve solo se vuole creare un altro
                annuncio. */}
            <Link
              to="/dashboard/property/new"
              className="shrink-0 inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[12.5px] font-medium px-3 py-1.5 rounded-md transition-colors min-h-[36px]"
              data-testid="new-property-btn"
            >
              <Plus size={13} weight="bold" />
              <span className="hidden sm:inline">Nuovo</span>
            </Link>
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonGrid count={6} />
      ) : isEmpty ? (
        <div
          className="mt-2 bg-white border border-[#EAE7DF] rounded-2xl px-6 py-10 lg:px-10 lg:py-14 text-center"
          data-testid="empty-state"
        >
          <div className="mx-auto w-14 h-14 rounded-full bg-[#F1F4ED] flex items-center justify-center mb-5">
            <Microphone size={22} weight="duotone" className="text-[#7C8B6E]" />
          </div>
          <h2 className="text-[22px] sm:text-[24px] font-semibold text-[#111827] tracking-tight">
            Crea il primo annuncio in 30 secondi
          </h2>
          <p className="mt-2 text-[13.5px] text-[#6B7280] max-w-md mx-auto leading-relaxed">
            Descrivi la casa con la tua voce. Pronto trascrive, riempie i campi e ti porta al cantiere dell&apos;annuncio.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Link
              to="/dashboard/welcome"
              className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white text-[13.5px] font-medium px-5 py-2.5 rounded-full transition-colors"
              data-testid="empty-cta-voice"
            >
              <Microphone size={14} weight="fill" /> Parla a Pronto
            </Link>
            <Link
              to="/dashboard/property/new"
              className="inline-flex items-center gap-1.5 text-[13px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium px-3 py-2"
              data-testid="empty-cta-create"
            >
              <Plus size={12} weight="bold" /> Compila a mano
            </Link>
            {demoProp ? (
              <Link
                to={`/dashboard/property/${demoProp.id}`}
                className="inline-flex items-center gap-1.5 text-[13px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium px-3 py-2"
                data-testid="empty-cta-demo"
              >
                Vedi un esempio <ArrowRight size={12} weight="bold" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/properties/seed-demo");
                    toast.success("Esempio caricato — puoi modificarlo o eliminarlo quando vuoi");
                    if (data?.property_id) {
                      load();
                    }
                  } catch (err) {
                    const detail = err?.response?.data?.detail;
                    toast.error(detail || "Caricamento esempio non riuscito");
                  }
                }}
                className="inline-flex items-center gap-1.5 text-[13px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium px-3 py-2 underline decoration-dotted underline-offset-4"
                data-testid="empty-cta-load-demo"
              >
                Carica un esempio
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="properties-grid">
          {filtered.map((p) => {
            const badge = statusBadge(p);
            const cover = authedFileUrl(p.cover_path, false, 640);
            // iter v28 r184 — User M1104: «se clicco annuncio già attivo,
            // portami direttamente su social/materiali, non su carica foto».
            // Annuncio "attivo" = ha pagina pubblica online → puntiamo a
            // tab=social (è il flow tipico dopo aver caricato foto: creare
            // post/reel per i social).
            const isActive = !!p.public_url;
            const defaultTab = isActive ? "social" : "foto";
            // v34 · 29 ago 2026 · User: «durante la generazione non voglio
            // poter entrare, se no si impalla». Se questa e' la property
            // in creazione, disabilitiamo click + overlay spinner.
            const isCreating = creatingPid && p.id === creatingPid;
            if (isCreating) {
              return (
                <div
                  key={p.id}
                  className="group relative bg-white border border-[#EAE7DF] rounded-lg overflow-hidden cursor-not-allowed opacity-90"
                  data-testid={`property-card-${p.id}`}
                  aria-busy="true"
                  aria-disabled="true"
                  title="Sto generando l'annuncio · attendi ancora qualche secondo"
                >
                  <div className="relative aspect-[16/10] bg-[#F4F1E8] overflow-hidden">
                    {cover && (
                      <img
                        src={cover}
                        alt=""
                        className="relative w-full h-full object-cover blur-[2px] scale-105"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#1C1C1A]/45 backdrop-blur-[1px] text-white text-center px-4">
                      <div className="w-9 h-9 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                      <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold">
                        Sto generando
                      </div>
                      <div className="text-[11px] text-white/90 leading-tight">
                        Aspetta che l'annuncio sia pronto
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">
                      {p.title || "Nuovo annuncio"}
                    </div>
                    <div className="text-[11.5px] text-[#7C8B6E] mt-0.5">
                      Torna tra qualche secondo · non chiudere l&apos;app
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={p.id}
                to={`/dashboard/property/${p.id}?tab=${defaultTab}`}
                onClick={() => rememberBack(p.id, "Tutti gli immobili", "/dashboard/annunci")}
                className="group relative bg-white border border-[#EAE7DF] rounded-lg overflow-hidden hover:border-[#C8C5BD] hover:shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 transition-all"
                data-testid={`property-card-${p.id}`}
              >
                {/* iter 309 r2: PropertyCardActions ora visibile ANCHE sulla
                    demo, così l'utente può eliminarla dalla lista senza
                    dover entrare nel dettaglio. */}
                <PropertyCardActions property={p} onChanged={load} />
                <div className="relative aspect-[16/10] bg-[#F4F1E8] overflow-hidden">
                  {/* Branded placeholder — sempre dietro, visibile se cover assente o img rotta */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-[#9A968B] gap-2 px-4 text-center">
                    <div className="w-10 h-10 rounded-full bg-white/80 border border-[#EAE7DF] flex items-center justify-center">
                      <ImageIcon size={18} weight="light" className="text-[#7C8B6E]" />
                    </div>
                    <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">
                      Aggiungi le foto
                    </div>
                    <div className="text-[10px] text-[#9A968B] -mt-1">
                      30 secondi · le sistema Pronto
                    </div>
                  </div>
                  {cover && (
                    <img
                      src={cover}
                      alt=""
                      className="relative w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <span className={`absolute top-2.5 left-2.5 inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.1em] font-bold px-2 py-0.5 rounded-full ${TONES[badge.tone]}`} data-testid={`status-${p.id}`}>
                    <span className="text-[7px]">●</span>{badge.label}
                  </span>
                  {p.marketplace_listing_id && (
                    <span className="absolute top-9 left-2.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.1em] font-bold px-2 py-0.5 rounded-full bg-[#5A6A4D] text-white shadow-sm" data-testid={`mkt-badge-${p.id}`}>
                      ⭐ Da Marketplace
                    </span>
                  )}
                  {p.leads_count > 0 && (
                    <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1C1C1A] text-white">
                      {p.leads_count} <Users size={9} weight="bold" />
                    </span>
                  )}
                  {/* iter v28 r300 — badge "novità" contatore: somma quanti
                      tab di questo immobile hanno aggiornamenti non visti
                      dall'utente (foto nuove, kit pronto, lead, social, ecc).
                      Si posiziona sotto al badge leads (o in alto-destra se
                      leads_count è 0) per non sovrapporsi. */}
                  {workspaceBadges[p.id] > 0 && (
                    <span
                      className={`absolute right-2.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#7C8B6E] text-white shadow-sm animate-pulse ${
                        p.leads_count > 0 ? "top-9" : "top-2.5"
                      }`}
                      data-testid={`property-badge-new-${p.id}`}
                      title={`${workspaceBadges[p.id]} novità da vedere`}
                    >
                      {workspaceBadges[p.id]} <Sparkle size={9} weight="fill" />
                    </span>
                  )}
                  {p.is_demo && (
                    <span className="absolute bottom-2.5 left-2.5 text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-white/90 text-[#1C1C1A] backdrop-blur-sm" data-testid={`demo-badge-${p.id}`}>
                      Esempio
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-medium">{p.property_type || "Immobile"}</span>
                    {p.listing_type === "affitto" && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.14em] bg-[#3B7BBF]/12 text-[#3B7BBF] px-1.5 py-0.5 rounded">Affitto</span>
                    )}
                    {/* iter v28 r209+r210 — Badge + Quick-switcher stile vetrina:
                        click sul badge → menu rapido con i 4 stili, cambio
                        istantaneo senza entrare nell'annuncio. */}
                    <StyleQuickSwitcher property={p} onChanged={load} />
                  </div>
                  <h3 className="text-[14.5px] font-semibold text-[#111827] mt-1 leading-tight tracking-tight line-clamp-2">{p.title}</h3>
                  <div className="mt-1.5 text-[12px] text-[#6B7280]">
                    {p.surface ? `${p.surface} m²` : "—"} · {p.rooms || "—"} locali · {p.city || p.address || "—"}
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#EAE7DF] flex items-center justify-between">
                    <div className="text-[15px] font-semibold text-[#111827]">
                      {formatPrice(p.price)}
                      {p.listing_type === "affitto" && p.price ? <span className="text-[11px] font-normal text-[#9A968B]">/mese</span> : null}
                    </div>
                    <div className="text-[11px] text-[#7C8B6E] font-medium inline-flex items-center gap-1">
                      <Sparkle size={10} weight="fill" />
                      {p.photos_enhanced_count || 0}/{p.photos_count || 0} foto
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-[13px] text-[#6B7280]">
              Nessun immobile trovato{search ? ` per "${search}"` : ""}.
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
